import { useSelector } from "react-redux";
import { selectParameters } from "../../../../store/selectors/new/parameters";
import React, { useCallback, useEffect, useState } from "react";
import { TApiParameter } from "../../../../api/endpoints/Parameter";
import { TApiSubset } from "../../../../api/endpoints/Subset";
import { TRootState } from "../../../../store/reducers/RootReducer";
import { TApiSet } from "../../../../api/endpoints/Set";
import * as Api from "../../../../api";
import { find } from "lodash";
import { Button, Table } from "react-bootstrap";
import Checkbox from "../../../Inputs/Checkbox/Checkbox";
import { formatParameterNameWithShortname, formatSubsetLabel } from "../../../../utils/Format";
import SubsetTypes from "../../../../utils/SubsetTypes";
import { TParameterSelection } from "../../index";
import Select from "react-select";
import { ValueType } from "react-select/lib/types";
import { Nullable } from "../../../../../types/utils";

interface ParameterSelectionProps {
  onSelect: (selection: Array<TParameterSelection>) => void;
}

const ParameterSelection = (props: ParameterSelectionProps) => {
  const { onSelect } = props;
  const params = Object.values(useSelector(selectParameters));
  const [selectedParam, setSelectedParam] = useState<Nullable<TApiParameter>>(null);
  const [subsetsToSelect, setSubsetsToSelect] = useState<Array<TApiSubset>>([]);
  const [selectedSubsetIds, setSelectedSubsetIds] = useState<Array<number>>([]);
  const setsById = useSelector<TRootState, Record<number, TApiSet>>(state => state.sets.data.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {} as Record<number, TApiSet>));

  useEffect(() => {
    if (selectedParam !== null) {
      Api.Subset.list({
        paramShortnames: [selectedParam.shortname]
      }).then((subsets => {
        setSubsetsToSelect(subsets)
      }));
    }
  }, [selectedParam]);

  const onParamSearchChange = (param: ValueType<TApiParameter>) => {
    const typedParam = param as Nullable<TApiParameter>;

    if (!typedParam) {
      setSelectedParam(null);
      setSubsetsToSelect([]);
      setSelectedSubsetIds([]);
    } else {
      setSelectedParam(typedParam);
    }
  }

  const onCheckboxChange = useCallback((subsetId: number) => (checked: boolean) => {
    if (checked) {
      setSelectedSubsetIds([...selectedSubsetIds, subsetId]);
    } else {
      setSelectedSubsetIds(selectedSubsetIds.filter(id => id !== subsetId))
    }
  }, [selectedSubsetIds]);

  const onCancel = useCallback(() => {
    onSelect([]);
  }, [onSelect]);

  const onConfirm = () => {
    if (!selectedParam) {
      return;
    }

    const param = selectedParam;

    const results = selectedSubsetIds.reduce((acc, selectedSubsetId) => {
      const subset = find(subsetsToSelect, { id: selectedSubsetId });
      if (!subset) return acc;

      acc.push({
        parameter: param,
        subset,
        set: setsById[subset.set_id]
      });

      return acc;
    }, [] as Array<TParameterSelection>);

    onSelect(results);
  }

  return (
    <div className="eu-cut-out-parameter-selection">
      <div className="margin-bottom-md">
        <label className="control-label">
          Select parameter
        </label>

        <Select<TApiParameter>
          name="params"
          options={params}
          getOptionLabel={(param) => param.name}
          getOptionValue={(param) => param.shortname}
          formatOptionLabel={formatParameterNameWithShortname}
          value={selectedParam}
          onChange={onParamSearchChange}
          isSearchable
          isClearable
        />
      </div>

      {
        subsetsToSelect.length > 0 &&
        <div>
          <label className="control-label">
            Select subsets
          </label>

          <div className="list-wrapper">
            <Table>
              <tbody>
                {
                  subsetsToSelect.map(subset => {
                    return (
                      <tr key={subset.id}>
                        <td>
                          <Checkbox
                            name={subset.id.toString()}
                            value={subset.id}
                            checked={selectedSubsetIds.includes(subset.id)}
                            onChange={onCheckboxChange(subset.id)}
                            label=""
                          />
                        </td>
                        <td>
                          {
                            formatSubsetLabel(subset)
                          }
                          <div className="set-description">
                            {setsById[subset.set_id]?.description}
                          </div>
                        </td>
                        <td>
                          {SubsetTypes.humanize(subset.type)}
                        </td>
                      </tr>
                    )
                  })
                }
              </tbody>
            </Table>
          </div>
        </div>
      }


      <div className="actions">
        <Button variant="light" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm}>
          Add parameter
        </Button>
      </div>
    </div>
  )
}

export default ParameterSelection;
